import { useMemo, useState } from 'react'

type Step =
  | 'tipo_solicitacao'
  | 'vinculo'
  | 'inquilino_contrato'
  | 'inquilino_firma'
  | 'inquilino_govbr'
  | 'inquilino_doc_imovel'
  | 'inquilino_docs_solicitante'
  | 'proprietario_doc_imovel'
  | 'proprietario_doc_vendedor'
  | 'proprietario_docs_solicitante'
  | 'inventario_termo'
  | 'inventario_com_termo'
  | 'inventario_sem_termo'
  | 'inventario_validar_autorizacao'
  | 'procuracao_poderes'
  | 'procuracao_confirmacao'
  | 'resumo_final'

type FlowState = {
  tipoSolicitacao: string
  vinculo: string
  documentosObrigatorios: string[]
  documentosComplementares: string[]
  pendencias: string[]
  historico: { step: Step; answer: string }[]
}

type Question = {
  title: string
  helper?: string
  options: string[]
}

const initialState: FlowState = {
  tipoSolicitacao: '',
  vinculo: '',
  documentosObrigatorios: [],
  documentosComplementares: [],
  pendencias: [],
  historico: [],
}

const COMMON_DOC_OPTIONS = ['Documentos apresentados', 'Falta documento com foto', 'Falta matrícula ou foto do hidrômetro', 'Faltam ambos']

function addUnique(list: string[], value: string) {
  return value && !list.includes(value) ? [...list, value] : list
}

function addMany(list: string[], values: string[]) {
  return values.reduce((acc, value) => addUnique(acc, value), list)
}

function questionFor(step: Step): Question | null {
  switch (step) {
    case 'tipo_solicitacao':
      return {
        title: 'Qual solicitação deseja orientar?',
        helper: 'Troca de Titularidade e Religação a Pedido seguem o mesmo fluxo documental.',
        options: ['Troca de Titularidade', 'Religação a Pedido'],
      }
    case 'vinculo':
      return { title: 'Qual é o vínculo do solicitante com o imóvel?', options: ['Inquilino', 'Proprietário', 'Inventário', 'Procuração Pública'] }
    case 'inquilino_contrato':
      return { title: 'O cliente possui contrato de locação?', options: ['Sim', 'Não'] }
    case 'inquilino_firma':
      return { title: 'O contrato de locação possui firma reconhecida do locador?', options: ['Sim', 'Não'] }
    case 'inquilino_govbr':
      return { title: 'O contrato foi assinado pelo GOV.BR?', options: ['Sim', 'Não'] }
    case 'inquilino_doc_imovel':
      return {
        title: 'Qual documento do imóvel em nome do locador será apresentado?',
        helper: 'É necessário comprovar o vínculo do locador com o imóvel.',
        options: ['IPTU', 'Escritura', 'Contrato de compra e venda com firma reconhecida', 'Certidão de matrícula atualizada até 30 dias', 'Nenhum documento'],
      }
    case 'inquilino_docs_solicitante':
    case 'proprietario_docs_solicitante':
      return {
        title: 'O solicitante apresentou documento oficial com foto e matrícula ou foto do hidrômetro?',
        options: COMMON_DOC_OPTIONS,
      }
    case 'proprietario_doc_imovel':
      return {
        title: 'Qual documento será apresentado para comprovar o vínculo do proprietário com o imóvel?',
        options: ['IPTU', 'Escritura', 'Contrato de compra e venda com firma reconhecida', 'Nenhum documento'],
      }
    case 'proprietario_doc_vendedor':
      return {
        title: 'Qual documento do imóvel em nome do vendedor será apresentado?',
        helper: 'Essa etapa é exigida quando foi apresentado contrato de compra e venda.',
        options: ['IPTU', 'Escritura', 'Certidão de matrícula atualizada até 30 dias', 'Nenhum documento'],
      }
    case 'inventario_termo':
      return { title: 'Foi apresentado Termo de Inventariante?', options: ['Sim', 'Não'] }
    case 'inventario_com_termo':
      return {
        title: 'Confira a documentação do inventário com Termo de Inventariante.',
        helper: 'Obrigatórios: Termo de Inventariante, documento do imóvel em nome do falecido, documento pessoal do inventariante e matrícula ou foto do hidrômetro.',
        options: ['Documentos apresentados', 'Falta documento do imóvel', 'Falta documento do inventariante', 'Falta matrícula ou foto do hidrômetro', 'Há mais de uma pendência'],
      }
    case 'inventario_sem_termo':
      return {
        title: 'Confira a documentação do inventário sem Termo de Inventariante.',
        helper: 'Obrigatórios: certidão de óbito, documento do imóvel em nome do falecido e autorização dos herdeiros.',
        options: ['Documentos apresentados', 'Falta certidão de óbito', 'Falta documento do imóvel', 'Falta autorização dos herdeiros', 'Há mais de uma pendência'],
      }
    case 'inventario_validar_autorizacao':
      return { title: 'A autorização dos herdeiros está assinada conforme documento, pelo GOV.BR ou possui firma reconhecida?', options: ['Sim', 'Não'] }
    case 'procuracao_poderes':
      return { title: 'A procuração pública concede poderes para administrar, alugar, transferir, representar ou regularizar o imóvel?', options: ['Sim', 'Não'] }
    case 'procuracao_confirmacao':
      return {
        title: 'Confirmação da Procuração Pública',
        helper: 'A procuração pública pode ser aceita quando comprovar que o outorgante concedeu poderes suficientes para representação do imóvel. Considera-se que a propriedade foi previamente comprovada perante o cartório.',
        options: ['Prosseguir', 'Registrar pendência'],
      }
    default:
      return null
  }
}

function commonSolicitanteDocs(state: FlowState, answer: string): FlowState {
  let next = { ...state, documentosObrigatorios: addMany(state.documentosObrigatorios, ['Documento oficial com foto do solicitante', 'Matrícula ou foto do hidrômetro']) }
  if (answer === 'Falta documento com foto') next = { ...next, pendencias: addUnique(next.pendencias, 'Documento oficial com foto do solicitante pendente') }
  if (answer === 'Falta matrícula ou foto do hidrômetro') next = { ...next, pendencias: addUnique(next.pendencias, 'Matrícula ou foto do hidrômetro pendente') }
  if (answer === 'Faltam ambos') next = { ...next, pendencias: addMany(next.pendencias, ['Documento oficial com foto do solicitante pendente', 'Matrícula ou foto do hidrômetro pendente']) }
  return next
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

export function TitularidadeReligacaoFlow() {
  const [step, setStep] = useState<Step>('tipo_solicitacao')
  const [state, setState] = useState<FlowState>(initialState)
  const [message, setMessage] = useState('')

  const question = questionFor(step)
  const isSummary = step === 'resumo_final'
  const progress = isSummary ? 100 : Math.min(92, 12 + state.historico.length * 14)

  const specificValidation = useMemo(() => {
    return state.tipoSolicitacao === 'Religação a Pedido'
      ? 'Ao final do procedimento, o colaborador deve certificar-se de que a OS de religação foi gerada de forma automática.'
      : 'O colaborador deve obrigatoriamente certificar-se de que não existe OS de corte ou consumo final aberta na matrícula. Caso exista, solicitar à Liderança o cancelamento dessa OS antes de prosseguir.'
  }, [state.tipoSolicitacao])

  const summaryText = useMemo(() => {
    const lines = [
      `Solicitação: ${state.tipoSolicitacao || '—'}`,
      `Vínculo com o imóvel: ${state.vinculo || '—'}`,
      '',
      'Documentos obrigatórios:',
      ...(state.documentosObrigatorios.length ? state.documentosObrigatorios.map((item) => `- ${item}`) : ['- Nenhum documento obrigatório registrado.']),
      '',
      'Documentos complementares:',
      ...(state.documentosComplementares.length ? state.documentosComplementares.map((item) => `- ${item}`) : ['- Nenhum documento complementar registrado.']),
      '',
      'Pendências:',
      ...(state.pendencias.length ? state.pendencias.map((item) => `- ${item}`) : ['- Sem pendências registradas conforme respostas informadas.']),
      '',
      `Validação específica: ${specificValidation}`,
      '',
      'Atenção: É obrigatória a conferência da categoria de uso do imóvel, como Comercial ou Residencial. Caso seja identificada alguma divergência em relação ao contrato do cliente, a categoria deverá ser corrigida no campo “Economia”, disponível na tela “Ligações” do SCAE.',
      '',
      'Observação: este procedimento não gera número de OS.',
    ]
    return lines.join('\n')
  }, [specificValidation, state])

  function restart() {
    setStep('tipo_solicitacao')
    setState(initialState)
    setMessage('')
  }

  function answer(value: string) {
    setMessage('')
    let next: FlowState = { ...state, historico: [...state.historico, { step, answer: value }] }
    let nextStep: Step = step

    switch (step) {
      case 'tipo_solicitacao':
        next.tipoSolicitacao = value
        nextStep = 'vinculo'
        break
      case 'vinculo':
        next.vinculo = value
        nextStep = value === 'Inquilino' ? 'inquilino_contrato' : value === 'Proprietário' ? 'proprietario_doc_imovel' : value === 'Inventário' ? 'inventario_termo' : 'procuracao_poderes'
        break
      case 'inquilino_contrato':
        next.documentosObrigatorios = addUnique(next.documentosObrigatorios, 'Contrato de locação')
        if (value === 'Não') {
          next.pendencias = addUnique(next.pendencias, 'Contrato de locação obrigatório')
          nextStep = 'resumo_final'
        } else nextStep = 'inquilino_firma'
        break
      case 'inquilino_firma':
        nextStep = value === 'Sim' ? 'inquilino_doc_imovel' : 'inquilino_govbr'
        break
      case 'inquilino_govbr':
        if (value === 'Não') next.documentosComplementares = addUnique(next.documentosComplementares, 'Documento pessoal do locador para conferência da assinatura')
        nextStep = 'inquilino_doc_imovel'
        break
      case 'inquilino_doc_imovel':
        if (value === 'Nenhum documento') next.pendencias = addUnique(next.pendencias, 'Documento do imóvel em nome do locador pendente')
        else next.documentosObrigatorios = addUnique(next.documentosObrigatorios, `${value} em nome do locador`)
        nextStep = 'inquilino_docs_solicitante'
        break
      case 'inquilino_docs_solicitante':
        next = commonSolicitanteDocs(next, value)
        nextStep = 'resumo_final'
        break
      case 'proprietario_doc_imovel':
        if (value === 'Nenhum documento') next.pendencias = addUnique(next.pendencias, 'Documento de propriedade do imóvel pendente')
        else next.documentosObrigatorios = addUnique(next.documentosObrigatorios, value)
        nextStep = value === 'Contrato de compra e venda com firma reconhecida' ? 'proprietario_doc_vendedor' : 'proprietario_docs_solicitante'
        break
      case 'proprietario_doc_vendedor':
        if (value === 'Nenhum documento') next.pendencias = addUnique(next.pendencias, 'Documento do imóvel em nome do vendedor pendente')
        else next.documentosObrigatorios = addUnique(next.documentosObrigatorios, `${value} em nome do vendedor`)
        nextStep = 'proprietario_docs_solicitante'
        break
      case 'proprietario_docs_solicitante':
        next = commonSolicitanteDocs(next, value)
        nextStep = 'resumo_final'
        break
      case 'inventario_termo':
        nextStep = value === 'Sim' ? 'inventario_com_termo' : 'inventario_sem_termo'
        break
      case 'inventario_com_termo':
        next.documentosObrigatorios = addMany(next.documentosObrigatorios, ['Termo de Inventariante', 'Documento do imóvel em nome do falecido', 'Documento pessoal do inventariante', 'Matrícula ou foto do hidrômetro'])
        if (value === 'Falta documento do imóvel') next.pendencias = addUnique(next.pendencias, 'Documento do imóvel em nome do falecido pendente')
        if (value === 'Falta documento do inventariante') next.pendencias = addUnique(next.pendencias, 'Documento pessoal do inventariante pendente')
        if (value === 'Falta matrícula ou foto do hidrômetro') next.pendencias = addUnique(next.pendencias, 'Matrícula ou foto do hidrômetro pendente')
        if (value === 'Há mais de uma pendência') next.pendencias = addMany(next.pendencias, ['Documento do imóvel em nome do falecido pendente', 'Documento pessoal do inventariante pendente', 'Matrícula ou foto do hidrômetro pendente'])
        nextStep = 'resumo_final'
        break
      case 'inventario_sem_termo':
        next.documentosObrigatorios = addMany(next.documentosObrigatorios, ['Certidão de óbito', 'Documento do imóvel em nome do falecido', 'Autorização dos herdeiros'])
        if (value === 'Falta certidão de óbito') next.pendencias = addUnique(next.pendencias, 'Certidão de óbito pendente')
        if (value === 'Falta documento do imóvel') next.pendencias = addUnique(next.pendencias, 'Documento do imóvel em nome do falecido pendente')
        if (value === 'Falta autorização dos herdeiros') next.pendencias = addUnique(next.pendencias, 'Autorização dos herdeiros pendente')
        if (value === 'Há mais de uma pendência') next.pendencias = addMany(next.pendencias, ['Certidão de óbito pendente', 'Documento do imóvel em nome do falecido pendente', 'Autorização dos herdeiros pendente'])
        nextStep = next.pendencias.length ? 'resumo_final' : 'inventario_validar_autorizacao'
        break
      case 'inventario_validar_autorizacao':
        if (value === 'Sim') next.documentosObrigatorios = addUnique(next.documentosObrigatorios, 'Documentos pessoais dos herdeiros')
        else next.pendencias = addUnique(next.pendencias, 'Regularização da autorização dos herdeiros pendente')
        nextStep = 'resumo_final'
        break
      case 'procuracao_poderes':
        next.documentosObrigatorios = addUnique(next.documentosObrigatorios, 'Procuração pública')
        if (value === 'Não') next.pendencias = addUnique(next.pendencias, 'Procuração sem poderes suficientes ou documentação complementar pendente')
        nextStep = 'procuracao_confirmacao'
        break
      case 'procuracao_confirmacao':
        if (value === 'Registrar pendência') next.pendencias = addUnique(next.pendencias, 'Procuração sem poderes suficientes ou documentação complementar pendente')
        nextStep = 'resumo_final'
        break
      default:
        break
    }

    setState(next)
    setStep(nextStep)
  }

  async function copySummary() {
    const ok = await copyText(summaryText)
    setMessage(ok ? 'Resumo copiado.' : 'Não foi possível copiar automaticamente.')
    window.setTimeout(() => setMessage(''), 1800)
  }

  return (
    <section className="tit-flow-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">FLOW GUIADO</p>
          <h2>Troca de Titularidade / Religação a Pedido</h2>
          <p className="muted">Valide vínculo, documentos e pendências antes de concluir o procedimento.</p>
        </div>
        <span className="status-chip">Sem OS própria</span>
      </div>

      <section className="tit-progress-card">
        <div className="tit-progress-head">
          <div><strong>{isSummary ? 'Orientação concluída' : 'Orientação em andamento'}</strong><span>{state.tipoSolicitacao || 'Selecione a solicitação para iniciar'}</span></div>
          <strong>{progress}%</strong>
        </div>
        <div className="tit-progress"><span style={{ width: `${progress}%` }} /></div>
        <div className="tit-context-chips">
          {state.tipoSolicitacao && <span>{state.tipoSolicitacao}</span>}
          {state.vinculo && <span>{state.vinculo}</span>}
          {state.pendencias.length > 0 && <span className="danger">{state.pendencias.length} pendência(s)</span>}
        </div>
      </section>

      {!isSummary && question && (
        <section className="tit-question-card">
          <p className="eyebrow">PRÓXIMA VALIDAÇÃO</p>
          <h3>{question.title}</h3>
          {question.helper && <p>{question.helper}</p>}
          <div className="tit-options">
            {question.options.map((option) => <button type="button" key={option} onClick={() => answer(option)}>{option}</button>)}
          </div>
        </section>
      )}

      {isSummary && (
        <section className="tit-summary-card">
          <header>
            <div><p className="eyebrow">RESULTADO DA ORIENTAÇÃO</p><h3>{state.pendencias.length ? 'Há pendências antes de prosseguir' : 'Documentação aparentemente completa'}</h3></div>
            <span className={`tit-result-badge ${state.pendencias.length ? 'danger' : 'ok'}`}>{state.pendencias.length ? 'NÃO PROSSEGUIR' : 'PODE PROSSEGUIR'}</span>
          </header>

          <div className="tit-summary-grid">
            <section><span>Solicitação</span><strong>{state.tipoSolicitacao || '—'}</strong></section>
            <section><span>Vínculo</span><strong>{state.vinculo || '—'}</strong></section>
          </div>

          <div className="tit-lists-grid">
            <section>
              <h4>Documentos obrigatórios</h4>
              <ul>{(state.documentosObrigatorios.length ? state.documentosObrigatorios : ['Nenhum documento obrigatório registrado.']).map((item) => <li key={item}>{item}</li>)}</ul>
            </section>
            <section>
              <h4>Documentos complementares</h4>
              <ul>{(state.documentosComplementares.length ? state.documentosComplementares : ['Nenhum documento complementar registrado.']).map((item) => <li key={item}>{item}</li>)}</ul>
            </section>
          </div>

          <section className={`tit-pending-box ${state.pendencias.length ? 'danger' : 'ok'}`}>
            <h4>Pendências identificadas</h4>
            <ul>{(state.pendencias.length ? state.pendencias : ['Sem pendências registradas conforme respostas informadas.']).map((item) => <li key={item}>{item}</li>)}</ul>
          </section>

          <section className="tit-warning-box"><strong>Validação específica</strong><p>{specificValidation}</p></section>
          <section className="tit-warning-box emphasis"><strong>Conferência obrigatória da categoria de uso</strong><p>Confira se o imóvel está corretamente classificado como Comercial ou Residencial. Se houver divergência em relação ao contrato do cliente, corrija a categoria no campo <b>Economia</b>, na tela <b>Ligações</b> do SCAE.</p></section>
          <section className="tit-note-box"><strong>Observação obrigatória</strong><p>Este procedimento não gera número de OS.</p></section>

          <div className="tit-summary-actions">
            <button className="primary-button" type="button" onClick={() => void copySummary()}>Copiar resumo</button>
            <button className="secondary-button" type="button" onClick={restart}>Reiniciar fluxo</button>
          </div>
          {message && <div className="tool-status ok">{message}</div>}
        </section>
      )}

      {!isSummary && state.historico.length > 0 && <button className="secondary-button tit-restart" type="button" onClick={restart}>Reiniciar fluxo</button>}
    </section>
  )
}
